'use client';

import { useEffect, useRef, type ReactNode } from 'react';

/** Pixels per second the track drifts on its own — a slow, readable pace. */
const SPEED = 26;
/** Quiet period after a manual scroll before the drift picks up again. */
const RESUME_DELAY_MS = 900;
/** Movement past this many pixels counts as a drag, not a click. */
const DRAG_THRESHOLD = 6;

/**
 * Infinite, self-scrolling project track.
 *
 * The children are three identical copies of the same list. The scroller is
 * parked in the middle copy and wrapped back whenever it leaves that band, so
 * there is always a full list of slack in both directions — the loop never
 * hits a scroll boundary, and because the copies are identical the jump is
 * invisible. Two copies would be enough to drift forward forever but would
 * dead-end the moment someone dragged backwards.
 *
 * Motion yields to the reader: it stops under the cursor, stops while a link
 * inside has keyboard focus, stops off-screen or on a hidden tab, and stands
 * down for a moment after any manual scroll. `prefers-reduced-motion` disables
 * the drift entirely — the track stays a plain, scrollable, draggable strip.
 */
export function ProjectMarquee({
  children,
  label,
}: {
  children: ReactNode;
  /** Accessible name for the scrollable region. */
  label: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    /** Sub-pixel position we own; `scrollLeft` alone rounds and would stall. */
    let pos = 0;
    let raf = 0;
    let lastFrame = 0;
    let resumeAt = 0;

    let held = false;
    let pointerInside = false;
    let focusInside = false;
    let onScreen = false;

    let dragStartX = 0;
    let dragStartScroll = 0;
    let dragDistance = 0;

    /** Width of one copy of the list. */
    const copyWidth = () => el.scrollWidth / 3;

    /**
     * Keep the scroller inside the middle copy. Returns the shift applied so
     * an in-flight drag can move its anchor by the same amount.
     */
    const wrap = () => {
      const w = copyWidth();
      if (!(w > 0)) return 0;

      let shift = 0;
      if (el.scrollLeft >= 2 * w) shift = -w;
      else if (el.scrollLeft < w) shift = w;

      if (shift !== 0) {
        pos = el.scrollLeft + shift;
        el.scrollLeft = pos;
        dragStartScroll += shift;
      }
      return shift;
    };

    const park = () => {
      const w = copyWidth();
      if (!(w > 0)) return;
      pos = w;
      el.scrollLeft = pos;
    };

    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);

      const dt = lastFrame ? now - lastFrame : 0;
      lastFrame = now;

      if (
        held ||
        pointerInside ||
        focusInside ||
        reduced.matches ||
        now < resumeAt
      ) {
        return;
      }

      const w = copyWidth();
      if (!(w > 0)) return;

      // Clamp dt so a backgrounded tab does not resume with a huge jump.
      pos += (SPEED * Math.min(dt, 50)) / 1000;
      if (pos >= 2 * w) pos -= w;
      el.scrollLeft = pos;
    };

    const start = () => {
      if (raf) return;
      lastFrame = 0;
      raf = requestAnimationFrame(frame);
    };

    const stop = () => {
      if (!raf) return;
      cancelAnimationFrame(raf);
      raf = 0;
    };

    const sync = () => {
      if (onScreen && document.visibilityState === 'visible') start();
      else stop();
    };

    /* --- manual scrolling (wheel, trackpad, touch, keyboard) ------------- */

    const onScroll = () => {
      // Our own writes land within a pixel of `pos`; anything else is a human.
      if (Math.abs(el.scrollLeft - pos) > 2) {
        pos = el.scrollLeft;
        if (!held) resumeAt = performance.now() + RESUME_DELAY_MS;
      }
      wrap();
    };

    /* --- mouse drag ------------------------------------------------------ */
    // Touch is left to native scrolling: it already has momentum and rubber
    // banding that no hand-rolled drag can match.

    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      held = true;
      dragStartX = event.clientX;
      dragStartScroll = el.scrollLeft;
      dragDistance = 0;
      // Without this the drag paints a text selection across every card it
      // crosses. Toggled on the node rather than through state so the track
      // never re-renders mid-gesture.
      el.classList.add('select-none');
      el.setPointerCapture(event.pointerId);
    };

    // Images and links are natively draggable; that gesture would steal the
    // pointer and drop a ghost image on the page.
    const onDragStart = (event: Event) => event.preventDefault();

    const onPointerMove = (event: PointerEvent) => {
      if (!held) return;
      const dx = event.clientX - dragStartX;
      dragDistance = Math.max(dragDistance, Math.abs(dx));
      el.scrollLeft = dragStartScroll - dx;
      pos = el.scrollLeft;
      wrap();
    };

    const endDrag = (event: PointerEvent) => {
      if (!held) return;
      held = false;
      resumeAt = performance.now() + RESUME_DELAY_MS;
      el.classList.remove('select-none');
      if (el.hasPointerCapture(event.pointerId)) {
        el.releasePointerCapture(event.pointerId);
      }
    };

    // A drag that ends on a card must not also open that card's link.
    const onClickCapture = (event: MouseEvent) => {
      if (dragDistance <= DRAG_THRESHOLD) return;
      event.preventDefault();
      event.stopPropagation();
      dragDistance = 0;
    };

    /* --- attention ------------------------------------------------------- */

    const onEnter = () => {
      pointerInside = true;
    };
    const onLeave = () => {
      pointerInside = false;
    };
    const onFocusIn = () => {
      focusInside = true;
    };
    const onFocusOut = () => {
      focusInside = false;
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        onScreen = entry?.isIntersecting ?? false;
        sync();
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);

    const resizeObserver = new ResizeObserver(() => wrap());
    resizeObserver.observe(el);

    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);
    el.addEventListener('click', onClickCapture, true);
    el.addEventListener('dragstart', onDragStart);
    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    el.addEventListener('focusin', onFocusIn);
    el.addEventListener('focusout', onFocusOut);
    document.addEventListener('visibilitychange', sync);

    park();

    return () => {
      stop();
      observer.disconnect();
      resizeObserver.disconnect();
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', endDrag);
      el.removeEventListener('pointercancel', endDrag);
      el.removeEventListener('click', onClickCapture, true);
      el.removeEventListener('dragstart', onDragStart);
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
      el.removeEventListener('focusin', onFocusIn);
      el.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  return (
    <div
      ref={ref}
      // A scrollable region needs a name and a tab stop to be reachable
      // without a mouse.
      role="region"
      aria-label={label}
      tabIndex={0}
      // `relative` is load-bearing, not decoration. An absolutely positioned
      // descendant is only clipped by an ancestor that is also its containing
      // block, so without this every abspos node inside the track — the
      // `sr-only` labels on the project links, most of all — escaped the
      // scroller, landed ~9,500px to the right on the third copy, and dragged
      // the *document's* scroll width out with it. Positioning the scroller
      // makes its own clipping authoritative for everything it holds.
      className="no-scrollbar edge-fade relative flex cursor-grab overflow-x-auto overscroll-x-contain active:cursor-grabbing"
    >
      {children}
    </div>
  );
}
